/**
 * Top Mega-menu — board "2 · Top Mega-menu". The menu's top level is a row of
 * MODULES across the top (a dark band under the host's bar); each module
 * drops one wide panel listing everything inside it at ANY depth — tiles for
 * its own screens, a column per sub-folder, deeper folders nested inside the
 * columns — with Pinned and Recently opened in the panel's aside. No side
 * menu competes with the screen for width.
 *
 * Open editors: a drop-down with a count badge in the HOST bar (published via
 * `setTopBarSlots`), plus the chip bar along the bottom. The screen title
 * carries the breadcrumb of the folders the screen sits in; a folder crumb
 * opens its module's panel at that folder.
 *
 * Full screen draws the ShellFrame alone. The ShellFrame keeps its position
 * in the tree in both modes, so toggling full screen never remounts the
 * screen (or its kept-alive editors).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { tw } from '../../testHooks'
import { EditorsTray } from '../nav/parts/EditorsList'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { Breadcrumb, ScreenTitle } from '../nav/parts/ScreenTitle'
import { ShellFrame } from '../nav/ShellFrame'
import { S } from '../nav/tokens'
import type { RunNavItem } from '../nav/types'
import { EditorsButton, EditorsPopover } from './megamenu/EditorsDropdown'
import { MegaPanel, type MegaFocusRequest } from './megamenu/MegaPanel'
import { ModulesBar, hasPanel, type PanelFocus } from './megamenu/ModulesBar'
import { MEGA_CSS } from './megamenu/styles'
import type { LayoutTopBarSlots, ShellLayoutProps } from './types'

const PANEL_ID = 'tw-mega-panel'
const EDITORS_POPUP_ID = 'tw-mega-editors'

export default function MegaMenuLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage, setTopBarSlots,
}: ShellLayoutProps) {
  const full = model.fullScreen
  const current = model.current
  const barRef = useRef<HTMLDivElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const edBtnRef = useRef<HTMLButtonElement>(null)

  // ── View state: which module's panel is open, and the editors drop-down ──
  const [openId, setOpenId] = useState<string | null>(null)
  const [focusReq, setFocusReq] = useState<MegaFocusRequest | null>(null)
  const [edOpen, setEdOpen] = useState(false)

  const setOpen = useCallback((id: string | null, focus?: PanelFocus) => {
    setOpenId(id)
    setFocusReq(id && focus ? { seq: Date.now(), target: focus } : null)
    if (id) setEdOpen(false)
  }, [])
  const focusModule = useCallback((id: string | null) => {
    if (!id) return
    const el = [...(barRef.current?.querySelectorAll<HTMLElement>(':scope > [role="menuitem"]') || [])]
      .find((b) => b.dataset.nodeId === id)
    el?.focus()
  }, [])
  const closePanel = useCallback((refocus: boolean) => {
    const was = openId
    setOpen(null)
    if (refocus) focusModule(was)
  }, [openId, setOpen, focusModule])

  const openNode = openId ? model.roots.find((n) => n.item.id === openId) : undefined
  // A module that left the navigation (rights) takes its panel with it.
  useEffect(() => { if (openId && !openNode) setOpenId(null) }, [openId, openNode])

  // Opening a screen (from the panel, a pin, the breadcrumb, a smart-page
  // link) or going full screen puts every drop-down away.
  const curId = current?.id ?? null
  const prevCur = useRef(curId)
  useEffect(() => {
    if (prevCur.current === curId) return
    prevCur.current = curId
    setOpenId(null)
    setEdOpen(false)
  }, [curId])
  useEffect(() => { if (full) { setOpenId(null); setEdOpen(false) } }, [full])

  // The panel closes on a click outside the modules row + panel, and when
  // focus leaves them (Tab past the aside).
  useEffect(() => {
    if (!openId) return undefined
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return
      setOpenId(null)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [openId])

  // Left/Right inside a panel: the neighbouring module (panels follow; a
  // screen module just takes focus).
  const onSide = useCallback((dir: -1 | 1) => {
    const roots = model.roots
    const i = roots.findIndex((n) => n.item.id === openId)
    if (i < 0 || !roots.length) return
    const next = roots[(i + dir + roots.length) % roots.length]
    if (hasPanel(next)) setOpen(next.item.id, 'first')
    else { setOpen(null); focusModule(next.item.id) }
  }, [model.roots, openId, setOpen, focusModule])

  // The module the open screen lives in (its marker), at any depth.
  const currentRootId = useMemo(() => {
    if (!current) return null
    return (model.pathOf(current)[0] || current).id
  }, [current, model])

  // A folder crumb: open its module's panel, landing on that folder's entries.
  const revealFolder = useCallback((folder: RunNavItem) => {
    const root = model.pathOf(folder)[0] || folder
    const node = model.roots.find((n) => n.item.id === root.id)
    if (!node || !hasPanel(node)) return
    setOpen(node.item.id, root.id === folder.id ? 'first' : { folderId: folder.id })
  }, [model, setOpen])

  // ── Open editors: the drop-down in the host bar ──
  const toggleEditors = useCallback(() => {
    setEdOpen((v) => !v)
    setOpenId(null)
  }, [])
  const closeEditors = useCallback((refocus: boolean) => {
    setEdOpen(false)
    if (refocus) edBtnRef.current?.focus()
  }, [])
  // "Open editors" from the cap notice: drop the list down.
  useEffect(() => {
    if (!model.editorsListRequest || !model.editorsEnabled) return
    setOpenId(null)
    setEdOpen(true)
  }, [model.editorsListRequest]) // eslint-disable-line react-hooks/exhaustive-deps

  const edCount = model.editors.length
  const edDirty = model.editors.filter((s) => s.dirty).length
  const slots = useMemo<LayoutTopBarSlots | null>(() => (model.editorsEnabled ? {
    right: (
      <EditorsButton btnRef={edBtnRef} count={edCount} dirty={edDirty} open={edOpen}
        onToggle={toggleEditors} popupId={EDITORS_POPUP_ID} />
    ),
  } : null), [model.editorsEnabled, edCount, edDirty, edOpen, toggleEditors])
  useEffect(() => {
    setTopBarSlots(slots)
    return () => setTopBarSlots(null)
  }, [slots, setTopBarSlots])

  const bandHeight = compact ? 42 : 48
  const titleHeight = compact ? 40 : 50
  const path = current ? model.pathOf(current) : []

  return (
    <div className="tw-mega" style={{
      position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, width: '100%', height: '100%',
      minHeight: 0, overflow: 'hidden', background: S.bg,
    }}>
      <style>{MEGA_CSS}</style>

      {!full && (
        <div
          ref={wrapRef}
          onBlur={(e) => {
            const next = e.relatedTarget as Node | null
            if (openId && next && !wrapRef.current?.contains(next)) setOpenId(null)
          }}
          // Above the screen (whose own sticky headers stack in the page) and
          // below the host bar and every window a screen opens.
          style={{ position: 'relative', zIndex: 8990, flexShrink: 0 }}
        >
          <ModulesBar model={model} barRef={barRef} openId={openId} setOpen={setOpen}
            currentRootId={currentRootId} panelIdFor={() => PANEL_ID} height={bandHeight}
            emptyText={emptyNavMessage} />
          {openNode && hasPanel(openNode) && (
            <MegaPanel key={openNode.item.id} model={model} node={openNode} panelId={PANEL_ID}
              onClose={closePanel} onSide={onSide} focusRequest={focusReq} />
          )}
        </div>
      )}

      <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {current && !full && (
          <div style={{ flexShrink: 0, background: S.panel, borderBottom: `1px solid ${S.border}` }}>
            {path.length > 0 && (
              <div style={{ padding: compact ? '6px 0.75rem 0' : '10px 1rem 0', minWidth: 0 }}>
                <Breadcrumb path={path} current={current} onFolder={revealFolder} />
              </div>
            )}
            <ScreenTitle
              item={current}
              bar={false}
              height={titleHeight}
              compact={compact}
              pinned={model.isPinned(current)}
              onTogglePin={() => model.togglePin(current)}
              fullScreen={full}
              onToggleFullScreen={() => model.setFullScreen(!full)}
              onClose={model.close}
              extras={headerExtras?.(current)}
            />
          </div>
        )}
        <ShellFrame
          model={model}
          renderObject={renderObject}
          padding={compact ? '0.5rem' : '0.5rem 1rem 1rem'}
          empty={<EmptyPane model={model} appTitle={appTitle} message={emptyPaneMessage} accent={themeColor} />}
        />
        {!full && model.editorsEnabled && <EditorsTray model={model} />}

        {/* The page behind an open panel dims; a click on it closes the panel
            (the outside-click rule) without reaching the screen. */}
        {!full && openNode && (
          <div aria-hidden {...tw('action', 'shell-mega-scrim')}
            onMouseDown={(e) => { e.preventDefault(); setOpenId(null) }}
            style={{ position: 'absolute', inset: 0, zIndex: 8980, background: 'var(--tw-mega-scrim)' }} />
        )}
      </div>

      {!full && edOpen && model.editorsEnabled && (
        <EditorsPopover model={model} btnRef={edBtnRef} popupId={EDITORS_POPUP_ID} onClose={closeEditors} />
      )}
    </div>
  )
}
